import React, { useRef, useEffect, PropsWithChildren } from 'react';
import { ScrollSentinel } from './HBKanbanInfiniteScroll.styles';

interface HBKanbanScrollSentinelProps {
    onLoadMore: () => void;
    hasMore: boolean;
    isLoading: boolean;
    testIdName?: string;
    columnId: string;
    rootMargin?: string;
}

/**
 * Component that uses Intersection Observer to trigger load more when visible
 */
export const HBKanbanInfiniteScroll = ({
    onLoadMore,
    hasMore,
    isLoading,
    rootMargin = '100px',
    children
}: PropsWithChildren<HBKanbanScrollSentinelProps>) => {
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!sentinelRef.current || isLoading || !hasMore) {
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                const [entry] = entries;
                const { boundingClientRect } = entry;
                // Fire if intersecting OR if element is above the current scroll position
                // This handles cases where fast scrolling causes the observer to miss the intersection
                const isAboveViewport = boundingClientRect.top < 0;
                if (entry.isIntersecting || isAboveViewport) {
                    onLoadMore();
                }
            },
            {
                root: null,
                rootMargin,
                threshold: 0
            }
        );

        const currentSentinel = sentinelRef.current;
        if (currentSentinel) {
            // Check if sentinel is already above viewport (could happen if we scrolled fast)
            const rect = currentSentinel.getBoundingClientRect();
            if (rect.top < 0) {
                onLoadMore();
            }

            observer.observe(currentSentinel);
        }

        return () => {
            if (currentSentinel) {
                observer.unobserve(currentSentinel);
            }
        };
    }, [hasMore, isLoading, onLoadMore, rootMargin]);

    if (!hasMore) {
        return null;
    }

    return (
        <ScrollSentinel ref={sentinelRef}>
            {isLoading && <>{children}</>}
        </ScrollSentinel>
    );
};

export default HBKanbanInfiniteScroll;
